import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getAuth,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithPopup,
  signOut,
  GoogleAuthProvider,
} from 'firebase/auth'
import { app } from '../services/firebase'
import { useAuth } from '../hooks/useAuth'

type Phase =
  | 'validating'
  | 'invalid'
  | 'expired'
  | 'ready'
  | 'accepting'
  | 'verify_email'
  | 'mismatch'

interface InviteInfo {
  email: string
  name: string | null
}

export function AcceptInvite() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { refreshUser, firebaseUser } = useAuth()
  const auth = getAuth(app)

  const [phase, setPhase] = useState<Phase>('validating')
  const [invite, setInvite] = useState<InviteInfo | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch(`/api/auth/invite/${token}`)
      .then(async (resp) => {
        if (cancelled) return
        if (resp.ok) {
          setInvite(await resp.json())
          setPhase('ready')
        } else if (resp.status === 410) {
          setPhase('expired')
        } else {
          setPhase('invalid')
        }
      })
      .catch(() => !cancelled && setPhase('invalid'))
    return () => {
      cancelled = true
    }
  }, [token])

  const acceptInvite = useCallback(async (): Promise<'ok' | 'unverified' | 'failed'> => {
    const fbUser = auth.currentUser
    if (!fbUser) {
      setError('Not signed in')
      return 'failed'
    }
    // Force refresh so a just-completed email verification is in the token
    const idToken = await fbUser.getIdToken(true)
    const resp = await fetch(`/api/auth/invite/${token}/accept`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
    })

    if (resp.ok) {
      await refreshUser()
      navigate('/', { replace: true })
      return 'ok'
    }

    const body = await resp.json().catch(() => ({}))
    if (body.code === 'email_unverified') {
      setPhase('verify_email')
      return 'unverified'
    }
    if (body.code === 'email_mismatch') {
      setPhase('mismatch')
    } else if (resp.status === 410) {
      setPhase('expired')
    } else if (resp.status === 409) {
      setError('This account is already registered — try logging in instead.')
    } else if (resp.status === 404) {
      setPhase('invalid')
    } else {
      setError(body.error || 'Something went wrong. Try again.')
    }
    return 'failed'
  }, [auth, token, refreshUser, navigate])

  const handleGoogle = async () => {
    setError(null)
    setBusy(true)
    try {
      await signInWithPopup(auth, new GoogleAuthProvider())
      setPhase('accepting')
      await acceptInvite()
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed')
      setPhase('ready')
    } finally {
      setBusy(false)
    }
  }

  const handleCreatePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (!invite) return
    setBusy(true)
    try {
      const cred = await createUserWithEmailAndPassword(auth, invite.email, password)
      await sendEmailVerification(cred.user)
      setPhase('verify_email')
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists — use "Continue with Google" or log in first, then reopen this link.')
      } else {
        setError(err.message || 'Could not create account')
      }
    } finally {
      setBusy(false)
    }
  }

  const handleVerifiedContinue = async () => {
    setError(null)
    setBusy(true)
    try {
      await auth.currentUser?.reload()
      const result = await acceptInvite()
      if (result === 'unverified') {
        setError("Not verified yet — check your inbox for the verification link.")
      }
    } finally {
      setBusy(false)
    }
  }

  const handleSignOut = async () => {
    await signOut(auth)
    setError(null)
    setPhase('ready')
  }

  const shell = (content: React.ReactNode) => (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm">{content}</div>
    </div>
  )

  if (phase === 'validating') {
    return shell(<p className="text-center text-[var(--color-text-secondary)]">Checking invitation...</p>)
  }

  if (phase === 'invalid') {
    return shell(
      <div className="text-center">
        <h1 className="text-2xl mb-4">Invalid invitation</h1>
        <p className="text-sm text-[var(--color-text-secondary)]">
          This invitation link isn't valid. It may have been replaced by a newer one — check
          for a more recent email, or ask your administrator for a new invitation.
        </p>
      </div>
    )
  }

  if (phase === 'expired') {
    return shell(
      <div className="text-center">
        <h1 className="text-2xl mb-4">Invitation expired</h1>
        <p className="text-sm text-[var(--color-text-secondary)]">
          This invitation has expired. Ask your administrator to resend it.
        </p>
      </div>
    )
  }

  if (phase === 'mismatch') {
    return shell(
      <div className="text-center">
        <h1 className="text-2xl mb-4">Different account</h1>
        <p className="text-sm text-[var(--color-text-secondary)] mb-6">
          This invitation was issued for <strong>{invite?.email}</strong>, but you're signed
          in with a different account.
        </p>
        <button onClick={handleSignOut} className="btn btn-secondary w-full">
          Sign out and try again
        </button>
      </div>
    )
  }

  if (phase === 'verify_email') {
    return shell(
      <div className="text-center">
        <h1 className="text-2xl mb-4">Verify your email</h1>
        <p className="text-sm text-[var(--color-text-secondary)] mb-6">
          We sent a verification link to <strong>{invite?.email}</strong>. Click it, then
          continue below.
        </p>
        {error && <p className="text-sm text-red-400 mb-4">{error}</p>}
        <button
          onClick={handleVerifiedContinue}
          disabled={busy}
          className="btn btn-primary w-full"
        >
          {busy ? 'Checking...' : "I've verified — continue"}
        </button>
      </div>
    )
  }

  // phase === 'ready' | 'accepting'
  const sessionMatchesInvite =
    firebaseUser?.email && invite?.email &&
    firebaseUser.email.toLowerCase() === invite.email.toLowerCase()

  return shell(
    <div>
      <h1 className="text-2xl mb-2 text-center">You're invited</h1>
      <p className="text-sm text-[var(--color-text-secondary)] text-center mb-8">
        Set up portal access for <strong>{invite?.email}</strong>
      </p>

      {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

      {firebaseUser ? (
        sessionMatchesInvite ? (
          <button
            onClick={() => { setPhase('accepting'); acceptInvite() }}
            disabled={busy}
            className="btn btn-primary w-full"
          >
            {phase === 'accepting' ? 'Setting up...' : `Continue as ${firebaseUser.email}`}
          </button>
        ) : (
          <div className="text-center">
            <p className="text-sm text-[var(--color-text-secondary)] mb-4">
              You're currently signed in as <strong>{firebaseUser.email}</strong>, but this
              invitation is for <strong>{invite?.email}</strong>.
            </p>
            <button onClick={handleSignOut} className="btn btn-secondary w-full">
              Sign out and continue as {invite?.email}
            </button>
          </div>
        )
      ) : (
        <>
          <button
            onClick={handleGoogle}
            disabled={busy}
            className="btn btn-secondary w-full"
          >
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 border-t border-[var(--color-border)]" />
            <span className="text-sm text-[var(--color-text-tertiary)]">or create a password</span>
            <div className="flex-1 border-t border-[var(--color-border)]" />
          </div>

          <form onSubmit={handleCreatePassword} className="space-y-4">
            <div>
              <label htmlFor="invite-password" className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Password
              </label>
              <input
                id="invite-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <div>
              <label htmlFor="invite-password-confirm" className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Confirm password
              </label>
              <input
                id="invite-password-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <button type="submit" disabled={busy} className="btn btn-primary w-full">
              {busy ? 'Creating account...' : 'Create account'}
            </button>
          </form>
        </>
      )}
    </div>
  )
}
